// crewbit-admin/src/lib/quoteMutations.ts
//
// Q2 Phase 2a (§T.108) — corrected quote model fully live (admin / operator).
// Substrate: migrations 327-330. RPCs:
//   create_quote (renamed from prior project-attached create RPC; sig reshape),
//   update_project_quote (drops 3 percentage args), respond_to_project_quote,
//   set_quote_line_items, set_payment_schedule_items (NEW).
// Mirrors crewbit-mobile/lib/quoteMutations.ts (paired-file pattern).
//
// Plain async functions, not a hook. Identity (created_by, business_id)
// resolves server-side via get_public_user_id() / get_user_business_id();
// the client never passes them. The wrapper (callMoneyRpc) handles errors.
//
// `set_quote_line_items` is replace-all: callers send the FULL desired list,
// RPC atomically deletes existing + bulk-inserts new + recomputes totals.

import { callMoneyRpc, type MoneyRpcResult } from './moneyRpc';
import { buildCanonicalSchedule, type CanonicalScheduleInput } from './paymentSchedule';
import type { QuoteLineItem } from '../types';

/**
 * Wire shape for `create_quote.p_line_items` and
 * `set_quote_line_items.p_line_items`. RPC accepts:
 *   - `description` — required
 *   - `quantity`, `unit_price` — numerics
 *   - `subtotal` — optional; server falls back to `quantity * unit_price`
 *   - `line_type` — optional; server defaults to 'labor' (RPC uses American
 *     spelling as fallback; admin UI uses 'labour'/'materials'/etc which pass
 *     through unchanged so existing data shape is preserved)
 *   - `unit`, `phase_id`, `sort_order` — optional
 */
export interface QuoteLineItemPayload {
  line_type?: string;
  description: string;
  quantity: number;
  unit?: string | null;
  unit_price: number;
  subtotal?: number;
  phase_id?: string | null;
  sort_order?: number;
  /** Q6 per-line tax fields (Wave 8 Step 1 mirror). Null = inherit header. */
  tax_type?: string | null;
  tax_rate?: number | null;
}

function toRpcLineItems(
  items: Array<QuoteLineItemPayload | QuoteLineItem | Partial<QuoteLineItem>>,
): Record<string, unknown>[] {
  return items.map((i, idx) => ({
    line_type: i.line_type,
    description: i.description,
    quantity: i.quantity,
    unit: i.unit ?? null,
    unit_price: i.unit_price,
    subtotal: 'subtotal' in i ? (i as { subtotal?: number }).subtotal : undefined,
    phase_id: i.phase_id ?? null,
    sort_order: 'sort_order' in i ? (i as { sort_order?: number }).sort_order : idx,
    tax_type: 'tax_type' in i ? (i as { tax_type?: string | null }).tax_type ?? null : null,
    tax_rate: 'tax_rate' in i ? (i as { tax_rate?: number | null }).tax_rate ?? null : null,
  }));
}

// ── create ──────────────────────────────────────────────────────────────────

export interface CreateProjectQuoteArgs {
  projectId: string | null;
  businessId?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  clientPhone?: string | null;
  /** REQUIRED non-empty array. RPC rejects with INVALID_LINE_ITEMS if empty. */
  lineItems: Array<QuoteLineItemPayload | Partial<QuoteLineItem>>;
  /**
   * Tax rate as FRACTION (0–1). Pass null to read from
   * `business_financial_settings.hst_rate` (recommended). RPC rejects values
   * > 1 with INVALID_TAX_RATE.
   */
  taxRate?: number | null;
  /** Default 30. Server sets `expires_at = current_date + valid_days`. */
  validDays?: number;
  /** Optional 3-row canonical schedule. Pass `null` for a service quote with no schedule. */
  paymentSchedule?: CanonicalScheduleInput | null;
  /** Free-text scope paragraph from the authoring sheet. Persisted on
   *  quotes.scope (Migration 334) and surfaced on get_quote_by_token + hosted
   *  pages. NULL/empty leaves quotes.scope untouched. */
  scope?: string | null;
}

export type CreateProjectQuoteResult = MoneyRpcResult<{
  quote_id: string;
  subtotal: number;
  tax_amount: number;
  total: number;
  share_token: string;
}>;

export async function createProjectQuote(
  args: CreateProjectQuoteArgs,
): Promise<CreateProjectQuoteResult> {
  return callMoneyRpc('create_quote', {
    p_business_id: args.businessId ?? null,
    p_project_id: args.projectId,
    p_client_id: args.clientId ?? null,
    p_client_name: args.clientName ?? null,
    p_client_phone: args.clientPhone ?? null,
    p_tax_rate: args.taxRate ?? null,
    p_valid_days: args.validDays ?? null,
    p_line_items: toRpcLineItems(args.lineItems),
    p_payment_schedule: args.paymentSchedule ? buildCanonicalSchedule(args.paymentSchedule) : null,
    p_scope: args.scope ?? null,
  });
}

// ── update (generic edit + status flip to sent) ─────────────────────────────

export interface UpdateProjectQuoteArgs {
  /**
   * Use this for `draft → sent` (server auto-stamps `sent_at`). Use
   * `respondProjectQuote()` for approve / decline.
   *
   * Payment-schedule edits no longer flow through this RPC — call
   * `setPaymentSchedule()` (NEW Phase 2a) which writes payment_schedule_items
   * directly. This RPC drops the 3 percentage args entirely.
   */
  status?: 'draft' | 'sent' | 'viewed';
  taxRate?: number | null;
  validDays?: number;
  clientNotes?: string | null;
  /** Server returns QUOTE_NOT_EDITABLE when scope (or any other field) is sent
   *  on a quote in `sent`/`viewed` — revoke to draft first. */
  scope?: string | null;
}

export type UpdateProjectQuoteResult = MoneyRpcResult<{
  quote_id: string;
  status: string;
  updated_fields: string[];
}>;

export async function updateProjectQuote(
  quoteId: string,
  args: UpdateProjectQuoteArgs,
): Promise<UpdateProjectQuoteResult> {
  return callMoneyRpc('update_project_quote', {
    p_quote_id: quoteId,
    p_status: args.status ?? null,
    p_tax_rate: args.taxRate ?? null,
    p_valid_days: args.validDays ?? null,
    p_client_notes: args.clientNotes ?? null,
    p_scope: args.scope ?? null,
  });
}

// ── set payment schedule (NEW Phase 2a — replace-all) ───────────────────────

export type SetPaymentScheduleResult = MoneyRpcResult<{
  quote_id: string;
  item_count: number;
}>;

export async function setPaymentSchedule(
  quoteId: string,
  schedule: CanonicalScheduleInput,
): Promise<SetPaymentScheduleResult> {
  return callMoneyRpc('set_payment_schedule_items', {
    p_quote_id: quoteId,
    p_schedule: buildCanonicalSchedule(schedule),
  });
}

// ── respond (approve / decline) ─────────────────────────────────────────────

export type RespondProjectQuoteResult = MoneyRpcResult<{
  quote_id: string;
  status: 'approved' | 'declined';
  /** True when the quote was already in the requested terminal state. Idempotent. */
  already_responded: boolean;
}>;

/**
 * Approve or decline. Requires current status `sent` or `viewed` —
 * QUOTE_NOT_RESPONDABLE on draft / approved / declined / expired (except
 * idempotent re-call of the same outcome). Server stamps approved_at /
 * declined_at + activity_log atomically.
 */
export async function respondProjectQuote(
  quoteId: string,
  outcome: 'approved' | 'declined',
  clientNotes?: string | null,
): Promise<RespondProjectQuoteResult> {
  return callMoneyRpc('respond_to_project_quote', {
    p_quote_id: quoteId,
    p_outcome: outcome,
    p_client_notes: clientNotes ?? null,
  });
}

// ── setLineItems (atomic replace-all) ───────────────────────────────────────

export type SetQuoteLineItemsResult = MoneyRpcResult<{
  quote_id: string;
  line_item_count: number;
  subtotal: number;
  tax_amount: number;
  total: number;
}>;

/**
 * Replace ALL line items on the quote in a single transaction. Recomputes
 * subtotal/tax/total + deposit/progress/final amounts using the quote's stored
 * tax_rate and percentages. Drafts only — QUOTE_NOT_EDITABLE otherwise.
 */
export async function setQuoteLineItems(
  quoteId: string,
  lineItems: Array<QuoteLineItemPayload | QuoteLineItem | Partial<QuoteLineItem>>,
): Promise<SetQuoteLineItemsResult> {
  return callMoneyRpc('set_quote_line_items', {
    p_quote_id: quoteId,
    p_line_items: toRpcLineItems(lineItems),
  });
}

// ── convert (approved quote → Job or Project) ───────────────────────────────

export type ConvertQuoteResult = MoneyRpcResult<{
  /** Present on convertQuoteToJob() */
  work_order_id?: string;
  /** Present on convertQuoteToProject() */
  project_id?: string;
  quote_id: string;
}>;

/**
 * Convert an approved quote to a Work Order. Server (§T.109, Migration 332)
 * enforces once-only via a UNION-ALL guard on work_orders.source_quote_id +
 * projects.source_quote_id — second conversion returns QUOTE_ALREADY_CONVERTED.
 * Status must be 'approved' — anything else returns QUOTE_NOT_APPROVED with
 * `current_status` in the error payload.
 */
export async function convertQuoteToJob(quoteId: string): Promise<ConvertQuoteResult> {
  return callMoneyRpc('convert_quote_to_job', { p_quote_id: quoteId });
}

/**
 * Convert an approved quote to a Project (budget = quote.total; hosts N work
 * orders against that budget). Same once-only guard + status gate as convertQuoteToJob().
 */
export async function convertQuoteToProject(quoteId: string): Promise<ConvertQuoteResult> {
  return callMoneyRpc('convert_quote_to_project', { p_quote_id: quoteId });
}

// ── cancel (Phase F §T.115 — operator-side soft cancel) ─────────────────────

export interface CancelQuoteArgs {
  quoteId: string;
  reason?: string | null;
}

export type CancelQuoteResult = MoneyRpcResult<{
  quote_id: string;
  status: 'cancelled';
  already_cancelled: boolean;
}>;

/**
 * Cancel a non-terminal quote. RPC enforces owner/manager + cancellable status
 * (`sent`/`viewed`/`changes_requested`). Returns QUOTE_NOT_CANCELLABLE with
 * `meta.current_status` for non-cancellable states.
 */
export async function cancelQuote(args: CancelQuoteArgs): Promise<CancelQuoteResult> {
  return callMoneyRpc('cancel_quote', {
    p_quote_id: args.quoteId,
    p_reason: args.reason ?? null,
  });
}
